import { ReactNode, useState } from 'react'
import { isNil } from 'lodash'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { useReselector } from '@/utils/sharedHooks'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { UploadDocumentType } from '@/constants/businessConstants'
import { getUserDocumentsByCategoryInternalName } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import {
  selectUserDocumentCategory,
  selectUserDocumentCategoryByInternalName,
} from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.selectors'

import { TypographyStyles, TextColors } from '@/styles'
import { Button, DocumentUploadDialog, Link, Popup, Text } from '@/ui'

interface IStateRegistrationUploadDialog {
  label: string
  value?: string | number | null
  popupContent?: ReactNode
  documentCat?: string
  documentType?: UploadDocumentType
  taxYear?: string
  additionalTrackingProperties?: Record<
    string,
    string | number | undefined | null
  >
  onUploadSuccess?: () => void
}

export const StateRegistrationUploadDialog = ({
  label,
  value,
  popupContent,
  documentCat,
  documentType = UploadDocumentType.TAX,
  taxYear,
  additionalTrackingProperties = {},
  onUploadSuccess,
}: IStateRegistrationUploadDialog) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const taxDocCat = useReselector(
    selectUserDocumentCategoryByInternalName,
    documentCat
  )

  const userDocs = useReselector(
    getUserDocumentsByCategoryInternalName,
    documentCat
  )

  const sentCategory = useReselector(selectUserDocumentCategory, taxDocCat?.id)

  const { uploadDocuments, isUploading } = useDocumentUpload({
    documentCategory: documentCat || '',
    documentType: documentType || UploadDocumentType.TAX,
    year: taxYear,
    onUploadSuccess: () => {
      setIsDialogOpen(false)
      onUploadSuccess?.()
    },
    additionalTrackingProperties: {
      document_subcategory: taxDocCat?.id.toString() || '',
      document_name: sentCategory?.title || '',
      tax_year: Number(taxYear),
      ...additionalTrackingProperties,
    },
  })

  const handleDocumentsUpload = async (documents: File[]) => {
    await uploadDocuments(documents)
  }

  return (
    <div className="flex flex-row text-left gap-2 mb-4 items-start">
      <Text className="w-full md:w-3/10" variant={TypographyStyles.LABEL_SM}>
        {label}
        {popupContent && <Popup content={popupContent} />}
      </Text>

      <div className="w-full md:w-7/10">
        {!isNil(value) && (
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {value}
          </Text>
        )}

        {userDocs.map((doc) =>
          doc?.signedUrl ? (
            <Link
              key={doc.id}
              href={doc.signedUrl}
              variant="primary"
              color={TextColors.MUTED}
            >
              {doc?.fileDescription}
            </Link>
          ) : (
            <Text
              key={doc.id}
              variant={TypographyStyles.BODY_SM}
              color={TextColors.MUTED}
            >
              {doc?.fileDescription}
            </Text>
          )
        )}

        {taxDocCat && !userDocs.length && (
          <Button
            variant="secondary"
            onClick={() => setIsDialogOpen(true)}
            iconLeft={regular('upload')}
            loading={isUploading}
            size="sm"
          >
            Upload
          </Button>
        )}
      </div>

      <DocumentUploadDialog
        cardTitle="State Registration Documents"
        cardDescription="State registrations for all states that you practice in."
        isOpen={isDialogOpen}
        onUpload={handleDocumentsUpload}
        onClose={() => setIsDialogOpen(false)}
      />
    </div>
  )
}
